/**
 * Gemini Live API — client helpers (ephemeral token + PCM utilities).
 * Session WebSocket lifecycle is driven from {@link useGeminiLiveVoiceConversationEngine}.
 */

import { firebaseApp } from '@healthdesk/shared-services';
import {
  GEMINI_LIVE_TOKEN_CALLABLE,
  GEMINI_LIVE_MODEL_DEFAULT,
  GEMINI_LIVE_FUNCTIONS_REGION,
} from './geminiLiveConstants';

/** Firebase returns these when Anonymous sign-in is off or disallowed for the project. */
const ANONYMOUS_AUTH_BLOCKED_CODES = new Set([
  'auth/admin-restricted-operation',
  'auth/operation-not-allowed',
]);

function enhanceAnonSignInError(err) {
  const code = err && err.code;
  if (!code || !ANONYMOUS_AUTH_BLOCKED_CODES.has(code)) return err;
  const hint =
    'Enable Anonymous sign-in: Firebase Console → Authentication → Sign-in method → Anonymous → Enable, then reload. ' +
    'Gemini Live (beta) needs a Firebase user to call the token API on public pages.';
  const wrapped = new Error(`${hint} (${code})`);
  wrapped.name = 'GeminiLiveAuthError';
  wrapped.cause = err;
  return wrapped;
}

/**
 * Cloud callables require an authenticated Firebase user. Public pages (e.g. standalone
 * symptom checker) may have no user until we create an anonymous session.
 * @returns {Promise<{ uid: string } | null>}
 */
export async function ensureFirebaseUserForLiveVoice() {
  if (typeof window === 'undefined') return null;
  const auth = firebaseApp.auth();
  if (auth.currentUser) return auth.currentUser;
  try {
    const cred = await auth.signInAnonymously();
    return cred.user;
  } catch (e) {
    const mapped = enhanceAnonSignInError(e);
    console.error('[GeminiLive] signInAnonymously failed', mapped.cause || mapped);
    throw mapped;
  }
}

/**
 * @param {{ model?: string, signInAnonymouslyIfNeeded?: boolean }} [options]
 * @returns {Promise<{ ephemeralToken: string, model: string, expireTime?: string }>}
 */
export async function fetchGeminiLiveEphemeralToken(options = {}) {
  const allowAnon = options.signInAnonymouslyIfNeeded !== false;
  if (allowAnon) {
    await ensureFirebaseUserForLiveVoice();
  }
  // Same Firebase app as @healthdesk/shared-services (Firestore, AuthProvider) + explicit region
  // so the callable URL matches deployment (wrong region → 404 → browser reports CORS on preflight).
  const fn = firebaseApp
    .functions(GEMINI_LIVE_FUNCTIONS_REGION)
    .httpsCallable(GEMINI_LIVE_TOKEN_CALLABLE);
  let res;
  try {
    res = await fn({
      model: options.model || GEMINI_LIVE_MODEL_DEFAULT,
    });
  } catch (e) {
    const code = e?.code;
    const isLocal =
      typeof window !== 'undefined' && window.location?.hostname === 'localhost';
    if (
      isLocal &&
      (code === 'internal' ||
        code === 'functions/internal' ||
        (e?.message && /CORS|Failed to fetch|NetworkError/i.test(e.message)))
    ) {
      const projectId = firebaseApp?.options?.projectId || '(unknown)';
      const hint =
        `Gemini Live (beta) token call failed from localhost (${code || 'error'}). ` +
        `Confirm REACT_APP_FIREBASE_PROJECT_ID matches the project that hosts this function (current app projectId: ${projectId}). ` +
        `In Firebase Console → Authentication → Settings, add "localhost" to Authorized domains. ` +
        `In Google Cloud Console → APIs & Services → Credentials, open the Firebase Browser API key and under Application restrictions allow HTTP referrer http://localhost:3000/* (and your port). ` +
        `See Cloud Function logs for getGeminiLiveEphemeralToken if the request reaches the server.`;
      const wrapped = new Error(hint);
      wrapped.cause = e;
      wrapped.code = code;
      throw wrapped;
    }
    throw e;
  }
  const d = res.data;
  if (!d?.ephemeralToken || !d?.model) {
    throw new Error('Invalid Gemini Live (beta) token response from server');
  }
  return d;
}

/**
 * @param {Float32Array} buffer
 * @param {number} sampleRate
 * @param {number} outSampleRate
 * @returns {Float32Array}
 */
export function downsampleBuffer(buffer, sampleRate, outSampleRate) {
  if (outSampleRate === sampleRate) return buffer;
  const ratio = sampleRate / outSampleRate;
  const newLength = Math.round(buffer.length / ratio);
  const result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < result.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
    let accum = 0;
    let count = 0;
    for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i += 1) {
      accum += buffer[i];
      count += 1;
    }
    result[offsetResult] = count ? accum / count : 0;
    offsetResult += 1;
    offsetBuffer = nextOffsetBuffer;
  }
  return result;
}

/**
 * @param {Float32Array} float32Arr
 * @returns {Int16Array}
 */
export function floatTo16BitPCM(float32Arr) {
  const len = float32Arr.length;
  const int16 = new Int16Array(len);
  for (let i = 0; i < len; i += 1) {
    const s = Math.max(-1, Math.min(1, float32Arr[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return int16;
}

/**
 * Encode little-endian PCM16 for Gemini Live sendRealtimeInput (expects base64 + mimeType).
 * @param {Int16Array} int16Arr
 * @returns {string}
 */
export function encodeLittleEndianPcm16Base64(int16Arr) {
  const u8 = new Uint8Array(int16Arr.buffer, int16Arr.byteOffset, int16Arr.byteLength);
  const chunk = 0x8000;
  let binary = '';
  for (let i = 0; i < u8.length; i += chunk) {
    binary += String.fromCharCode.apply(null, u8.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Decode base64 little-endian PCM to an AudioBuffer for playback.
 * @param {string} base64
 * @param {number} sampleRate e.g. 24000 for Live output
 * @param {AudioContext} audioContext
 */
export function decodeLittleEndianPcm16Base64(base64, sampleRate, audioContext) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const int16 = new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i += 1) {
    float32[i] = int16[i] / 0x8000;
  }
  const buffer = audioContext.createBuffer(1, float32.length, sampleRate);
  buffer.copyToChannel(float32, 0);
  return buffer;
}

/**
 * Parse sample rate from mime like "audio/pcm;rate=24000".
 * @param {string} [mimeType]
 * @param {number} fallback
 */
export function parsePcmRateFromMime(mimeType, fallback) {
  if (!mimeType || typeof mimeType !== 'string') return fallback;
  const m = /rate=(\d+)/i.exec(mimeType);
  return m ? parseInt(m[1], 10) : fallback;
}
